import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const logoVariants = cva(
  "flex items-center font-sans font-semibold",
  {
    variants: {
      size: {
        sm: "text-lg",
        default: "text-xl",
        lg: "text-2xl",
        xl: "text-3xl",
      },
      layout: {
        horizontal: "flex-row space-x-2",
        vertical: "flex-col space-y-1 items-center",
      },
    },
    defaultVariants: {
      size: "default",
      layout: "horizontal",
    },
  }
);

const iconVariants = cva(
  "bg-primary-600 dark:bg-accent-500 rounded-lg flex items-center justify-center text-white transition-colors duration-200",
  {
    variants: {
      size: {
        sm: "w-6 h-6 text-sm",
        default: "w-8 h-8 text-base",
        lg: "w-10 h-10 text-lg",
        xl: "w-12 h-12 text-xl",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

export const Logo = React.forwardRef(({
  className,
  size = "default",
  layout = "horizontal",
  showTagline = false,
  variant = "default", // default, white, dark
  ...props
}, ref) => {
  const getTextColor = () => {
    switch (variant) {
      case 'white':
        return 'text-white';
      case 'dark':
        return 'text-gray-900';
      default:
        return 'text-gray-900 dark:text-dark-900';
    }
  };

  const getIconColor = () => {
    switch (variant) {
      case 'white':
        return 'bg-white text-primary-600';
      case 'dark':
        return 'bg-gray-900 text-white';
      default:
        return 'bg-primary-600 dark:bg-accent-500 text-white';
    }
  };

  const getWiseColor = () => {
    switch (variant) {
      case 'white':
        return 'text-blue-200';
      case 'dark':
        return 'text-primary-600';
      default:
        return 'text-primary-600 dark:text-cyan-400';
    }
  };

  const getTaglineColor = () => {
    switch (variant) {
      case 'white':
        return 'text-gray-300';
      case 'dark':
        return 'text-gray-500';
      default:
        return 'text-gray-500 dark:text-dark-600';
    }
  };

  return (
    <div
      ref={ref}
      className={cn(logoVariants({ size, layout }), className)}
      {...props}
    >
      <div className={cn(iconVariants({ size }), getIconColor())}>
        <i className="fa-solid fa-brain" />
      </div>
      
      <div className={layout === 'vertical' ? 'text-center' : ''}>
        <div className={cn("font-semibold transition-colors duration-200", getTextColor())}>
          Organise<span className={cn("transition-colors duration-200", getWiseColor())}>Wise</span>
        </div>
        {showTagline && (
          <div className={cn(
            "text-xs font-normal transition-colors duration-200",
            getTaglineColor(),
            layout === 'vertical' ? 'mt-0' : 'mt-0.5'
          )}>
            AI-powered organization
          </div>
        )}
      </div>
    </div>
  );
});

Logo.displayName = "Logo";

// Icon-only version of the logo
export const LogoIcon = React.forwardRef(({
  className,
  size = "default",
  variant = "default",
  ...props
}, ref) => {
  const getIconColor = () => {
    switch (variant) {
      case 'white':
        return 'bg-white text-primary-600';
      case 'dark':
        return 'bg-gray-900 text-white';
      default:
        return 'bg-primary-600 dark:bg-accent-500 text-white';
    }
  };

  return (
    <div
      ref={ref}
      className={cn(iconVariants({ size }), getIconColor(), className)}
      {...props}
    >
      <i className="fa-solid fa-brain" />
    </div>
  );
});

LogoIcon.displayName = "LogoIcon";

// Text-only version of the logo
export const LogoText = React.forwardRef(({
  className,
  size = "default",
  variant = "default",
  showTagline = false,
  ...props
}, ref) => {
  const getTextColor = () => {
    switch (variant) {
      case 'white':
        return 'text-white';
      case 'dark':
        return 'text-gray-900';
      default:
        return 'text-gray-900 dark:text-dark-900';
    }
  };

  const getWiseColor = () => {
    switch (variant) {
      case 'white':
        return 'text-blue-200';
      case 'dark':
        return 'text-primary-600';
      default:
        return 'text-primary-600 dark:text-cyan-400';
    }
  };

  const getTaglineColor = () => {
    switch (variant) {
      case 'white':
        return 'text-gray-300';
      case 'dark':
        return 'text-gray-500';
      default:
        return 'text-gray-500 dark:text-dark-600';
    }
  };

  const sizeMap = {
    sm: 'text-lg',
    default: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-3xl',
  };

  return (
    <div ref={ref} className={cn("font-sans", className)} {...props}>
      <div className={cn("font-semibold transition-colors duration-200", sizeMap[size], getTextColor())}>
        Organise<span className={cn("transition-colors duration-200", getWiseColor())}>Wise</span>
      </div>
      {showTagline && (
        <div className={cn("text-xs font-normal mt-0.5 transition-colors duration-200", getTaglineColor())}>
          AI-powered organization
        </div>
      )}
    </div>
  );
});

LogoText.displayName = "LogoText"; 